/**
 * ClientCycleReport — Phase 4 client-facing shared cycle report (Spanish).
 *
 * Renders an in-app banner when the trainer has shared a cycle report.
 * Clicking opens a friendly, pedagogical recap modal. Dismissing marks the
 * report seen (banner won't show again).
 *
 * CLIENT copy = 100% Spanish (client-facing boundary). Never shows internal
 * trainer notes — the backend feed already strips them.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CycleReviewExperience from './CycleReviewExperience';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientCycleReport = () => {
  const [report, setReport] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cycles/client/shared-report`);
      setReport(r.data || null);
    } catch (e) {
      console.error('[ClientCycleReport] failed', e);
      setReport(null);
    }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const dismiss = useCallback(async () => {
    if (!report?.cycle_id) return;
    try {
      await axios.post(`${API}/cycles/${report.cycle_id}/seen`);
    } catch (e) {
      console.error('[ClientCycleReport] dismiss failed', e);
    }
    setShowModal(false);
    setReport(null);
  }, [report]);

  if (!report) return null;

  return (
    <>
      <div
        className="mb-6 rounded-xl border border-purple-400/40 bg-gradient-to-r from-purple-600/25 to-blue-600/25 px-5 py-4 flex items-center justify-between gap-4"
        data-testid="client-cycle-report-banner"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-2xl">🎉</span>
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm">
              Tu entrenador ha compartido el resumen de tu ciclo
            </div>
            <div className="text-blue-200 text-xs capitalize truncate">
              {report.focus_es}{report.weeks ? ` · ${report.weeks} semanas` : ''}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white text-sm font-medium whitespace-nowrap"
            data-testid="client-cycle-report-open"
          >
            Ver resumen
          </button>
          <button
            onClick={dismiss}
            className="text-blue-200 hover:text-white text-xl leading-none px-1"
            title="Descartar"
            data-testid="client-cycle-report-dismiss-banner"
          >
            ×
          </button>
        </div>
      </div>

      {showModal && (
        <CycleReviewExperience
          report={report}
          onClose={() => setShowModal(false)}
          onDismiss={dismiss}
        />
      )}
    </>
  );
};

export default ClientCycleReport;
